<template>
  <div>
    <aside class="news-panel">
      <div class="news-header">
        <h3 class="news-heading mb-0">
          <b-icon icon="megaphone-fill" class="mr-2"></b-icon>Actualités
        </h3>
        <button
          v-if="isAdmin"
          type="button"
          class="news-add-btn"
          title="Ajouter une actualité"
          @click="openAddNews"
        >
          <b-icon icon="plus-lg"></b-icon>
        </button>
      </div>

      <p v-if="!newsItems.length" class="text-muted small mb-0 px-3 py-3">
        Aucune actualité pour le moment.
      </p>

      <ul v-else class="news-list">
        <li v-for="item in newsItems" :key="item.id" class="news-item">
          <div class="news-meta">
            <span class="news-date">{{ item.date }}</span>
            <span v-if="item.tag" class="news-tag" :class="`news-tag--${item.tagVariant || 'info'}`">
              {{ item.tag }}
            </span>
            <button
              v-if="isAdmin"
              type="button"
              class="news-delete-btn ml-auto"
              title="Archiver cette actualité"
              @click="deleteNews(item)"
            >
              <b-icon icon="archive"></b-icon>
            </button>
          </div>
          <div class="news-title">{{ item.title }}</div>
          <p v-if="item.text" class="news-text mb-0">{{ item.text }}</p>

          <div v-if="item.poll" class="news-poll">
            <template v-if="!hasVotedPoll(item)">
              <button
                v-for="option in item.poll.options"
                :key="option.id"
                type="button"
                class="poll-option"
                @click="votePoll(item, option)"
              >
                <span class="poll-option-label">
                  <b-icon icon="circle" class="poll-option-icon mr-2"></b-icon>
                  {{ option.label }}
                </span>
              </button>
            </template>

            <div v-else class="poll-thanks">
              <b-icon icon="check-circle-fill" class="poll-thanks-icon mr-2"></b-icon>
              <div>
                <div class="poll-thanks-text">Merci pour ton vote !</div>
                <button type="button" class="poll-change" @click="removePollVote(item)">
                  Modifier mon vote
                </button>
              </div>
            </div>

            <template v-if="isAdmin">
              <button type="button" class="poll-results-toggle" @click="togglePollResults(item)">
                <b-icon :icon="pollResultsVisible[item.id] ? 'eye-slash' : 'bar-chart-fill'" class="mr-1"></b-icon>
                {{ pollResultsVisible[item.id]
                  ? 'Masquer les résultats'
                  : `Voir les résultats (${pollTotal(item)} vote${pollTotal(item) > 1 ? 's' : ''})` }}
              </button>

              <div v-if="pollResultsVisible[item.id]" class="poll-results">
                <div v-for="option in item.poll.options" :key="`result-${option.id}`" class="poll-result" :id="`popover-target-${item.id}-${option.id}`">
                  <span class="poll-result-bar" :style="{ width: pollPercent(item, option) + '%' }"></span>
                  <span class="poll-result-label">{{ option.label }}</span>
                  <span class="poll-result-count">{{ option.votes }} · {{ pollPercent(item, option) }}%</span>
                  
                  <b-popover
                    v-if="option.votes"
                    :target="`popover-target-${item.id}-${option.id}`"
                    triggers="hover"
                    placement="top"
                    custom-class="poll-voters-popover"
                  >
                    <ul class="poll-voters-list">
                      <li v-for="v in option.voters" :key="v.id" class="poll-voters-item">
                        {{ v.name }} {{ v.surname }}
                      </li>
                    </ul>
                  </b-popover>
                </div>
              </div>
            </template>
          </div>

          <b-link
            v-if="item.link"
            class="news-link"
            :to="item.link.to"
            :href="item.link.href"
            :target="item.link.href ? '_blank' : undefined"
          >
            {{ item.link.label }}
            <b-icon
              :icon="item.link.href ? 'box-arrow-up-right' : 'arrow-right'"
              class="ml-1 news-link-icon"
            ></b-icon>
          </b-link>
        </li>
      </ul>
    </aside>

    <b-modal
      v-model="addNewsVisible"
      title="Nouvelle actualité"
      ok-title="Publier"
      cancel-title="Annuler"
      :ok-disabled="!newsForm.title.trim() || newsSaving"
      @ok="submitNews"
    >
      <b-form-group label="Titre *">
        <b-form-input v-model="newsForm.title" placeholder="Titre de l'actualité"></b-form-input>
      </b-form-group>

      <b-row>
        <b-col cols="7">
          <b-form-group label="Tag">
            <b-form-input v-model="newsForm.tag" placeholder="Club, Matos, Sondage…"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="5">
          <b-form-group label="Couleur du tag">
            <b-form-select v-model="newsForm.tagVariant" :options="tagVariantOptions"></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Texte">
        <b-form-textarea v-model="newsForm.text" rows="3" placeholder="Contenu de l'actualité"></b-form-textarea>
      </b-form-group>

      <b-form-group label="Lien (optionnel)">
        <b-form-input v-model="newsForm.linkLabel" class="mb-2" placeholder="Libellé du lien (ex : Faire un don)"></b-form-input>
        <b-form-input v-model="newsForm.linkHref" placeholder="https://…"></b-form-input>
      </b-form-group>

      <b-form-group
        label="Sondage (optionnel)"
        description="Une option par ligne, deux minimum. Laisser vide pour une actualité sans sondage."
      >
        <b-form-textarea v-model="newsForm.pollOptions" rows="3" placeholder="Oui&#10;Non"></b-form-textarea>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import session from "@/store/session";
import NewsService from '@/services/news.service';

export default {
  name: 'NewsPanel',
  data() {
    return {
      newsItems: [],
      pollVotes: {}, // { newsId: optionId } — vote de l'utilisateur courant
      pollResultsVisible: {}, // { newsId: bool } — résultats dépliés (admin)
      addNewsVisible: false,
      newsSaving: false,
      newsForm: {
        title: '',
        tag: '',
        tagVariant: 'info',
        text: '',
        linkLabel: '',
        linkHref: '',
        pollOptions: '',
      },
      tagVariantOptions: [
        { value: 'info', text: 'Bleu' },
        { value: 'warning', text: 'Orange' },
        { value: 'danger', text: 'Rouge' },
      ],
    }
  },
  computed: {
    isLogged() {
      return session.isLoggedIn();
    },
    isAdmin() {
      return this.isLogged && session.user.role === "admin";
    },
  },
  watch: {
    isAdmin: {
      immediate: true,
      handler(isAdmin) {
        if (isAdmin) {
          this.adminFetchNews();
        } else {
          this.fetchNews();
        }
      },
    },
  },
  methods: {
    async fetchNews() {
      try {
        const res = await NewsService.list();
        this.newsItems = res.data.map(item => ({
          ...item,
          date: this.formatNewsDate(item.date),
        }));
        this.pollVotes = {};
        this.newsItems.forEach(item => {
          if (item.poll && item.poll.votedOptionId) {
            this.$set(this.pollVotes, item.id, item.poll.votedOptionId);
          }
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des actualités :", error);
      }
    },
    async adminFetchNews() {
      try {
        const res = await NewsService.adminList();
        this.newsItems = res.data.map(item => ({
          ...item,
          date: this.formatNewsDate(item.date),
        }));
        this.pollVotes = {};
        this.newsItems.forEach(item => {
          if (item.poll && item.poll.votedOptionId) {
            this.$set(this.pollVotes, item.id, item.poll.votedOptionId);
          }
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des actualités :", error);
      }
    },
    formatNewsDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    },
    hasVotedPoll(item) {
      return this.pollVotes[item.id] != null;
    },
    pollTotal(item) {
      return item.poll.options.reduce((sum, option) => sum + option.votes, 0);
    },
    pollPercent(item, option) {
      const total = this.pollTotal(item);
      return total ? Math.round((option.votes * 100) / total) : 0;
    },
    togglePollResults(item) {
      this.$set(this.pollResultsVisible, item.id, !this.pollResultsVisible[item.id]);
    },
    async votePoll(item, option) {
      if (!this.isLogged) {
        this.$bvToast.toast('Vous devez être connecté pour voter.', {
          title: 'Attention',
          variant: 'warning',
          solid: true
        });
        return;
      }
      try {
        const res = await NewsService.vote(item.id, option.id);
        item.poll = res.data.poll;
        this.$set(this.pollVotes, item.id, option.id);
      } catch (error) {
        this.$bvToast.toast(error.message, { title: 'Erreur', variant: 'danger', solid: true });
      }
    },
    async removePollVote(item) {
      try {
        const res = await NewsService.vote(item.id, null);
        item.poll = res.data.poll;
        this.$delete(this.pollVotes, item.id);
      } catch (error) {
        this.$bvToast.toast(error.message, { title: 'Erreur', variant: 'danger', solid: true });
      }
    },
    openAddNews() {
      this.newsForm = {
        title: '',
        tag: '',
        tagVariant: 'info',
        text: '',
        linkLabel: '',
        linkHref: '',
        pollOptions: '',
      };
      this.addNewsVisible = true;
    },
    async submitNews(bvModalEvt) {
      bvModalEvt.preventDefault();
      const form = this.newsForm;
      const pollOptions = form.pollOptions
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);
      if (pollOptions.length === 1) {
        this.$bvToast.toast('Un sondage doit avoir au moins deux options.', {
          title: 'Attention',
          variant: 'warning',
          solid: true
        });
        return;
      }
      const payload = {
        title: form.title.trim(),
        tag: form.tag.trim(),
        tagVariant: form.tagVariant,
        text: form.text.trim(),
      };
      if (form.linkLabel.trim() && form.linkHref.trim()) {
        payload.link = { label: form.linkLabel.trim(), href: form.linkHref.trim() };
      }
      if (pollOptions.length) {
        payload.pollOptions = pollOptions;
      }
      this.newsSaving = true;
      try {
        const res = await NewsService.create(payload);
        this.newsItems.unshift({
          ...res.data,
          date: this.formatNewsDate(res.data.date),
        });
        this.addNewsVisible = false;
      } catch (error) {
        this.$bvToast.toast(error.message, { title: 'Erreur', variant: 'danger', solid: true });
      } finally {
        this.newsSaving = false;
      }
    },
    async deleteNews(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Archiver l'actualité « ${item.title} » ?`,
        {
          title: 'Confirmation',
          okTitle: 'Archiver',
          okVariant: 'danger',
          cancelTitle: 'Annuler',
          centered: true,
        }
      );
      if (!confirmed) return;
      try {
        await NewsService.remove(item.id);
        this.newsItems = this.newsItems.filter(news => news.id !== item.id);
      } catch (error) {
        this.$bvToast.toast(error.message, { title: 'Erreur', variant: 'danger', solid: true });
      }
    },
  },
}
</script>

<style scoped>
.news-panel {
  background: #f4f8fa;
  border: 1px solid var(--color-stroke-soft, #b8dde4);
  border-radius: 12px;
  overflow: hidden;
  position: sticky;
  top: 16px;
}

.news-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px 12px;
  background: var(--color-guc-darkblue, #056d80);
}

.news-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 8px;
  background: transparent;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
}

.news-add-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.news-add-btn:focus {
  outline: none;
}

.news-delete-btn {
  padding: 0;
  border: none;
  background: none;
  color: #b0bec5;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.15s;
}

.news-delete-btn:hover {
  color: #b02a37;
}

.news-delete-btn:focus {
  outline: none;
}

.news-heading {
  font-family: 'Gagalin', sans-serif;
  font-size: 1.15rem;
  color: #ffffff;
  display: flex;
  align-items: center;
}

.news-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.news-item {
  padding: 14px 18px;
  border-left: 3px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}

.news-item + .news-item {
  border-top: 1px solid var(--color-border-light, #e6eaee);
}

/* .news-item:hover {
  background: #e9f2f5;
  border-left-color: var(--color-guc-yellow, #ff914d);
} */

.news-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.news-date {
  font-size: 11px;
  font-weight: 700;
  color: #8a9aa5;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.news-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 10px;
  line-height: 1.4;
}

.news-tag--info {
  background: var(--color-guc-lightblue, #cdecef);
  color: var(--color-guc-darkblue, #056d80);
}

.news-tag--warning {
  background: #ffe8d9;
  color: #c25a1e;
}

.news-tag--danger {
  background: #fde2e4;
  color: #b02a37;
}

.news-title {
  font-size: 14px;
  font-weight: 700;
  color: #343a40;
  line-height: 1.3;
  margin-bottom: 3px;
}

.news-text {
  font-size: 13px;
  line-height: 1.45;
  color: #6c757d;
}

.news-link {
  display: inline-flex;
  align-items: center;
  margin-top: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-guc-darkblue, #056d80);
}

.news-link:hover {
  color: var(--color-guc-darkdarkblue, #035766);
  text-decoration: underline;
}

.news-link-icon {
  font-size: 10px;
}

/* --- Sondage dans une actualité --- */
.news-poll {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 8px;
}

.poll-option {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 5px 10px;
  border: 1px solid var(--color-stroke-soft, #b8dde4);
  border-radius: 8px;
  background: transparent;
  color: #495057;
  font-size: 12.5px;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.15s, background 0.15s;
}

.poll-option:hover {
  border-color: var(--color-stroke-strong, #82bfcb);
  background: rgba(205, 236, 239, 0.35);
}

.poll-option:focus {
  outline: none;
}

.poll-option-icon {
  font-size: 11px;
  color: var(--color-guc-darkblue, #056d80);
}

/* remerciement après le vote */
.poll-thanks {
  display: flex;
  align-items: flex-start;
  padding: 8px 10px;
  border: 1px solid var(--color-stroke-soft, #b8dde4);
  border-radius: 8px;
  background: rgba(205, 236, 239, 0.35);
}

.poll-thanks-icon {
  color: var(--color-guc-darkblue, #056d80);
  font-size: 15px;
  margin-top: 2px;
}

.poll-thanks-text {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-guc-darkdarkblue, #035766);
}

.poll-change {
  padding: 0;
  border: none;
  background: none;
  font-size: 11px;
  color: #8a9aa5;
  text-decoration: underline;
  cursor: pointer;
}

.poll-change:hover {
  color: var(--color-guc-darkblue, #056d80);
}

.poll-change:focus {
  outline: none;
}

/* résultats du sondage (admin) */
.poll-results-toggle {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-guc-darkblue, #056d80);
  cursor: pointer;
}

.poll-results-toggle:hover {
  color: var(--color-guc-darkdarkblue, #035766);
  text-decoration: underline;
}

.poll-results-toggle:focus {
  outline: none;
}

.poll-results {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.poll-result {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 10px;
  border: 1px solid var(--color-stroke-soft, #b8dde4);
  border-radius: 8px;
  font-size: 12px;
  color: #495057;
  overflow: hidden;
}

.poll-result-bar {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background: var(--color-guc-lightblue, #cdecef);
  opacity: 0.7;
  transition: width 0.3s ease;
}

.poll-result-label,
.poll-result-count {
  position: relative;
}

.poll-result-count {
  font-size: 11px;
  font-weight: 700;
  color: var(--color-guc-darkblue, #056d80);
  flex-shrink: 0;
}

/* popover listant les votants d'une option (survol des résultats, admin) */
.poll-voters-popover.popover {
  border: 1px solid var(--color-stroke-soft, #b8dde4);
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(5, 109, 128, 0.15);
  max-width: 220px;
}

.poll-voters-popover .popover-body {
  padding: 8px 10px;
  border-radius: 8px;
}

.poll-voters-popover.bs-popover-top > .arrow::before {
  border-top-color: var(--color-stroke-soft, #b8dde4);
}

.poll-voters-popover.bs-popover-top > .arrow::after {
  border-top-color: #ffffff;
}

.poll-voters-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.poll-voters-item {
  font-size: 12px;
  color: #495057;
  white-space: nowrap;
}

.poll-voters-item + .poll-voters-item {
  border-top: 1px solid var(--color-border-light, #e6eaee);
  padding-top: 3px;
}

@media (max-width: 991px) {
  .news-panel {
    position: static;
  }
}
</style>
